// src/components/Admin/ExportTab.js
import { useState } from "react";
import { Download, Search } from "lucide-react";
import { fetchForExport, toCsv, downloadCsv } from "../services/exportCsv";

// Edit statuses/columns to match your schema. columns: [] = export all fields.
const SOURCES = {
  jobs: {
    label: "Jobs",
    statuses: ["draft", "searching", "dispatched", "assigned", "on_the_way", "arrived", "completed", "cancelled", "no_tasker"],
    columns: [],
  },
  payments: { label: "Payments", statuses: ["paid", "pending", "failed", "refunded"], columns: [] },
  users: { label: "Users", statuses: [], columns: [] },
  reviews: { label: "Reviews", statuses: [], columns: [] },
  contactMessages: { label: "Contact messages", statuses: ["new", "resolved"], columns: [] },
  taskers: {
    label: "Providers (profiles)",
    statuses: [],
    columns: ["name.displayName", "phone.value", "location.lat", "location.lng"],
  },
};

const input =
  "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#006C35]";

export default function ExportTab() {
  const [source, setSource] = useState("jobs");
  const [status, setStatus] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const cfg = SOURCES[source];

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchForExport({ collectionName: source, from, to, status, search });
      setRows(data);
    } catch (err) {
      console.error("Export fetch error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const download = () => {
    const date = new Date().toISOString().slice(0, 10);
    downloadCsv(toCsv(rows, cfg.columns), `${source}_${status || "all"}_${date}.csv`);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h2 className="text-lg font-bold text-[#006C35] mb-4">Export to CSV</h2>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
        <select className={input} value={source}
          onChange={(e) => { setSource(e.target.value); setStatus(""); setRows(null); }}>
          {Object.entries(SOURCES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>

        <select className={input} value={status} onChange={(e) => setStatus(e.target.value)}
          disabled={!cfg.statuses.length}>
          <option value="">All statuses</option>
          {cfg.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>

        <input type="date" className={input} value={from} onChange={(e) => setFrom(e.target.value)} />
        <input type="date" className={input} value={to} onChange={(e) => setTo(e.target.value)} />

        <input className={input} placeholder="Search any field" value={search}
          onChange={(e) => setSearch(e.target.value)} />
      </div>

      <div className="flex items-center gap-3">
        <button onClick={run} disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg bg-[#535C79] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50">
          <Search size={16} /> {loading ? "Loading…" : "Preview"}
        </button>
        <button onClick={download} disabled={!rows?.length}
          className="inline-flex items-center gap-2 rounded-lg bg-[#006C35] px-4 py-2 text-sm font-medium text-white hover:bg-[#005428] disabled:opacity-50">
          <Download size={16} /> Download CSV
        </button>
        {rows && <span className="text-sm text-gray-600">{rows.length} records</span>}
      </div>

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
    </div>
  );
}